import type { ChatFileAttachment } from "@v2/features/chat/utils/chatFileAttachment";
import type {
  ChatEvent,
  ChatMessage,
  ChatMessageContent,
} from "schemas/generated/chatMessage";
import type { SelectedElementDesignationElementTransformed } from "schemas/generated/fileModifiers";

import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { toast } from "@replohq/lattice/components/toast/Toast";
import { skipToken } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import {
  useCurrentWorkspaceId,
  useIsAgentBillingEnabled,
} from "@v2/contexts/WorkspaceDashboardContext";
import { VIDEO_ACCEPT_FILE_EXTENSIONS } from "@v2/features/assets/constants";
import { batchUploadToCloudflare } from "@v2/features/assets/useUploadAsset";
import { useChatSidebar } from "@v2/features/chat/hooks/useChatSidebar";
import { useChatTurn } from "@v2/features/chat/hooks/useChatTurn";
import { useUpdatePageId } from "@v2/features/chat/hooks/useUpdatePageId";
import {
  useChatStoreActions,
  useIsChatLoading,
} from "@v2/features/chat/store/ChatStoreContext";
import { isValidChatAssetType } from "@v2/features/chat/utils/chatAssetValidation";
import { serializeChatToken } from "@v2/features/chat/utils/chatTokenSerialization";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { invalidateResourcesRelatedToDeployment } from "@v2/features/page-renderer/components/navbar/invalidateResourcesRelatedToDeployment";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { EditorStoreContext } from "@v2/store/editorStore";
import { agentBillingRouter } from "@v2/trpc/routes/agentBilling";
import { chatRouter, chatUtils } from "@v2/trpc/routes/chat";
import { fileVersionUtils } from "@v2/trpc/routes/fileVersion";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { useSearchParams } from "react-router-dom";
import { filterNulls } from "replo-utils/lib/array";
import { isNetworkErrorMessage } from "replo-utils/lib/errors";
import { parseJsonOrNull } from "replo-utils/lib/json";
import {
  chatCodeChangesOverviewItemSchema,
  chatGeneratedPageItemSchema,
  chatTokenSchema,
} from "schemas/chatMessage";
import { isChatTokenSegment, parseChatTokens } from "schemas/utils/chat";
import { v4 } from "uuid";

interface SplitStreamParams {
  chatTurnId: string;
  chatId: string;
}

/**
 * Extracts the pageId from a message potentially containing a generatedPage content item.
 *
 * If the message is not a generatedPage content item, returns null.
 *
 * If the message is a generatedPage content item, but fails its zod schema,
 * then it throws an error
 *
 * @author Max, 2026-01-26
 */
const extractGeneratedPageIdIfExists = (message: ChatMessage) => {
  if (message.role !== "assistant") {
    return null;
  }

  const generatedPageContentItem = message.content.find((contentItem) => {
    return (
      contentItem.type === "text" && contentItem.contentType === "generatedPage"
    );
  });

  if (!generatedPageContentItem || generatedPageContentItem.type !== "text") {
    return null;
  }

  const generatedPageData = chatGeneratedPageItemSchema.parse(
    parseJsonOrNull(generatedPageContentItem.text),
  );

  return generatedPageData.pageId;
};

export const useCurrentChat = () => {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const logEvent = useLogAnalytics();

  const [searchParams, setSearchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const chatId = searchParams.get("chatId");
  const isChatLoading = useIsChatLoading(chatId);

  const editorStore = useContext(EditorStoreContext);

  const {
    setStreamingState,
    addOrUpdateMessageForChat,
    updateToolCallProgressForChat,
    updateStreamingStatus,
  } = useChatStoreActions();

  const { updatePageId } = useUpdatePageId();

  const forceScrollToBottomRef = useRef<(() => void) | null>(null);
  const [files, setFiles] = useState<ChatFileAttachment[] | null>(null);

  const [splitStreamParams, setSplitStreamParams] =
    useState<SplitStreamParams | null>(null);

  const { setOpen: setIsChatSidebarOpen } = useChatSidebar();

  const { mutateAsync: createChat } = chatRouter.create.useMutation({
    onSuccess: () => {
      void chatUtils.getPaginatedChats.invalidate({
        projectId,
      });
    },
  });

  const { mutate: updateChatPageId } = chatRouter.updateChatPageId.useMutation({
    onSuccess: () => {
      void chatUtils.getPaginatedChats.invalidate({
        projectId,
      });
    },
  });

  const { mutate: generateTitle, isPending: isGeneratingTitle } =
    chatRouter.generateTitle.useMutation({
      onSuccess: () => {
        if (chatId) {
          void chatUtils.get.invalidate({
            chatId,
          });
          void chatUtils.getPaginatedChats.invalidate({
            projectId,
          });
        }
      },
    });

  const { addChatTurn } = useChatTurn();

  // NOTE (Bobby: 2025-07-13): We have two different calls for chat here. If there is already a chatId from the url, we want to fetch that one. Since they may be viewing a chat history someone else shared with them
  // If not, we want to get the user's chat OR create one if they've never had a chat for this page.
  const { data: chatFromUrl, error: chatError } = chatRouter.get.useQuery(
    chatId ? { chatId } : skipToken,
    // NOTE Bobby (2025-06-12): This is needed beacause our default trpc/react-query staleTime is infinity. This means when we initially fetch
    // with this query key, the messages will be empty/outdated so when you navigate to a different chat and come back,
    // it will be out of date. Easier to set this as 0 and have it refetch on backend every time since it's not expensive and less logic than invalidating everywhere we care to.
    // TODO Bobby (2025-06-12): We should change the default staleTime to 0
    // NOTE Kevin (2025-09-08): Suppress error notifications for chat not found since we handle this gracefully in the navigation useEffect below
    // by creating a new chat for the pageId if there is one.
    {
      staleTime: 0,
      throwOnError: false,
    },
  );

  const chat = chatFromUrl;

  const latestChatTurnId = chat?.messages.at(-1)?.chatTurnId;

  // NOTE (Gabe, 2026-01-27): Track attempted reconnection to prevent infinite loops
  const attemptedReconnectRef = useRef<string | null>(null);
  const splitStreamStartedRef = useRef<string | null>(null);

  // NOTE (Gabe, 2026-01-27): Attempt to reconnect to an in-progress stream on page load
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  useEffect(() => {
    if (!latestChatTurnId || !chatId) {
      return;
    }
    if (splitStreamParams) {
      return;
    }
    if (attemptedReconnectRef.current === latestChatTurnId) {
      return;
    }

    attemptedReconnectRef.current = latestChatTurnId;
    setSplitStreamParams({
      chatTurnId: latestChatTurnId,
      chatId,
    });
  }, [latestChatTurnId, chatId, splitStreamParams]);

  const workspaceId = useCurrentWorkspaceId();
  const isAgentBillingEnabled = useIsAgentBillingEnabled();
  const { mutateAsync: check } = agentBillingRouter.credits.check.useMutation();
  const { mutateAsync: track } =
    agentBillingRouter.credits.DEPRECATED_track.useMutation();

  // NOTE (Gabe, 2026-01-27): Split streaming flow - mutation to initialize,
  // then subscription to stream
  const { mutateAsync: initializeChatTurn } =
    chatRouter.initializeChatTurn.useMutation();

  chatRouter.streamChatTurnUpdates.useSubscription(
    splitStreamParams
      ? {
          chatTurnId: splitStreamParams.chatTurnId,
        }
      : skipToken,
    {
      onData: (trackedData) => {
        const splitChatId = splitStreamParams?.chatId;
        const splitChatTurnId = splitStreamParams?.chatTurnId;

        if (!splitChatId || !splitChatTurnId) {
          return;
        }

        if (splitStreamStartedRef.current !== splitChatTurnId) {
          splitStreamStartedRef.current = splitChatTurnId;
          setStreamingState(splitChatId, {
            isStreaming: true,
          });
        }

        logStreamEvent("SPLIT STREAM EVENT", trackedData.data, splitChatId);
        processChatEvent(trackedData.data, splitChatId);
      },
      onError: (error) => {
        if (document.visibilityState === "hidden") {
          return;
        }

        if (splitStreamParams?.chatId) {
          handleStreamError(splitStreamParams.chatId, error);
        }
        setSplitStreamParams(null);
      },
      onComplete: () => {
        if (splitStreamParams) {
          updateStreamingStatus(splitStreamParams.chatId, false);
        }
        setSplitStreamParams(null);
        invalidateResourcesRelatedToDeployment();
        void workspaceUtils.getChatMessagesUsage.invalidate();
      },
    },
  );

  const processChatEvent = useCallback(
    (chatEvent: ChatEvent, streamChatId: string) => {
      if (chatEvent.type === "message") {
        const messageData = chatEvent.data;
        const codeChangesOverviewContentItem = messageData.content.find(
          (contentItem) => {
            return (
              contentItem.type === "text" &&
              contentItem.contentType === "codeChangesOverview"
            );
          },
        );

        if (
          pageId &&
          messageData.role === "assistant" &&
          codeChangesOverviewContentItem?.type === "text"
        ) {
          const codeChangesOverview = chatCodeChangesOverviewItemSchema.parse(
            JSON.parse(codeChangesOverviewContentItem.text),
          );

          if (codeChangesOverview.versionId) {
            editorStore?.store
              .getState()
              .actions.setViewingVersionId(codeChangesOverview.versionId);
            void fileVersionUtils.findByFileId.invalidate({
              fileId: pageId!,
            });
          }
        }

        // NOTE (Max, 2026-01-22): Check if this is a page generation completion, and if yes,
        // redirect to the generate page by updating the params's page id
        const generatedPageId = extractGeneratedPageIdIfExists(messageData);
        if (generatedPageId) {
          updatePageId(generatedPageId);
        }

        if (messageData.role === "assistant" || messageData.role === "tool") {
          addOrUpdateMessageForChat(
            streamChatId,
            messageData.id,
            messageData.content,
            messageData.role,
            messageData.chatTurnId,
          );
        }
      } else if (chatEvent.type === "tool-progress") {
        const toolCallId = chatEvent.data.toolCallId;
        const progress = chatEvent.data.progress;
        updateToolCallProgressForChat(streamChatId, toolCallId, progress);
      }
    },
    [
      addOrUpdateMessageForChat,
      updateToolCallProgressForChat,
      updatePageId,
      editorStore,
      pageId,
    ],
  );

  const logStreamEvent = useCallback(
    (label: string, event: unknown, streamChatId: string) => {
      if (
        parseJsonOrNull(
          localStorage.getItem("replo.debug.v2.logChatStreamEventsToConsole") ??
            "false",
        )
      ) {
        console.info(label, { event, streamChatId });
      }
    },
    [],
  );

  const handleStreamError = useCallback(
    (streamChatId: string, error: unknown) => {
      console.error("Chat subscription error:", error);

      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isNetworkError = isNetworkErrorMessage(errorMessage);

      // NOTE (goleary, 2026-01-16): Error messages are saved by the backend with
      // the errorId for debugging. Frontend just shows a toast notification.
      toast.error({
        title: isNetworkError
          ? "Network error"
          : "An unknown error has occurred",
        description: isNetworkError
          ? "Network error while streaming chat. Please check your connection."
          : "There was an error processing your chat. Please try again or reach out to support@replo.app if this error persists.",
      });

      if (!isNetworkError) {
        analytics.logEvent("error.unknown.shown", {
          endpoint: "chat.stream",
          errorSource: "trpc",
        });
      }

      updateStreamingStatus(streamChatId, false);
    },
    [updateStreamingStatus],
  );

  // NOTE (Bobby: 2025-06-24): This function is used to create all the new user messages that will be added to the chat history. It is the user prompt as
  // well as the context items they've selected. This is added to the chat history on the frontend as well as sent to the backend to be persisted.
  const createNewUserMessage = useCallback(
    async ({
      currentPrompt,
      isHidden,
      chatId,
      projectId,
      defaultDesignation,
      filesToUpload,
      chatTurnId,
    }: {
      currentPrompt: string;
      isHidden: boolean;
      chatId: string;
      projectId: string;
      defaultDesignation?: SelectedElementDesignationElementTransformed;
      filesToUpload?: ChatFileAttachment[] | null;
      chatTurnId: string;
    }): Promise<{
      message: ChatMessage;
      numInlineAssetsAttached: number;
      numExternalAssetsAttached: number;
      numProductsAttached: number;
      numSelectedElementsAttached: number;
    }> => {
      const contextMessages: ChatMessageContent = [];

      let uploadedFiles: { file: File; assetUrl: string }[] = [];

      try {
        uploadedFiles = await batchUploadToCloudflare(
          (filesToUpload ?? []).map((attachment) => attachment.file),
          projectId,
        );
      } catch (error) {
        console.error("Failed to upload files:", error);
      }
      const externalAssets = uploadedFiles.map((uploadedFile) => {
        const fileExtension = uploadedFile.file.name.split(".").pop();
        return {
          url: uploadedFile.assetUrl,
          isValidChatAsset: isValidChatAssetType(uploadedFile.file),
          isVideo: fileExtension
            ? VIDEO_ACCEPT_FILE_EXTENSIONS.includes(
                `.${fileExtension.toLowerCase()}`,
              )
            : uploadedFile.file.type.startsWith("video/"),
        };
      });

      // This adds the asset urls that are inline in the prompt.
      // TODO (Bobby: 2025-06-27): Separate this out into a helper function
      const segments = parseChatTokens(currentPrompt);
      const inlineAssets = filterNulls(
        segments.filter(isChatTokenSegment).map((segment) => {
          const { token } = segment;

          if (token.type === "asset") {
            const { name, fileExtension, url } = token.data;

            const isValidChatAsset = isValidChatAssetType({
              name,
              type: fileExtension,
            });

            const isVideo = VIDEO_ACCEPT_FILE_EXTENSIONS.includes(
              `.${fileExtension.toLowerCase()}`,
            );

            return {
              url,
              isValidChatAsset,
              isVideo,
            };
          }
          return null;
        }),
      );

      // NOTE (Ben, 11/13/2025) We use the cloudflare worker image resizer to ensure we are sending the correct image size to the llm
      // https://docs.anthropic.com/en/docs/build-with-claude/vision#evaluate-image-size
      const resizedImageUrl = (imageUrl: string) => {
        const imageUrlObj = new URL(imageUrl);
        imageUrlObj.searchParams.set("height", "1568");
        imageUrlObj.searchParams.set("width", "1568");
        imageUrlObj.searchParams.set("fit", "scaledown");
        return imageUrlObj.toString();
      };

      externalAssets.forEach(({ url, isValidChatAsset, isVideo }) => {
        // NOTE (Kevin, 2025-12-12): Videos are sent as URL text for the LLM to pass
        // through (LLMs don't support video input). We add a video content item
        // for UI display in the chat.
        if (isVideo) {
          contextMessages.push({
            type: "text",
            text: `[Attached Video File] The user has attached a video file. Video URL: ${url}`,
            isHidden: true,
          });
          contextMessages.push({
            type: "video",
            video: url,
          });
        } else {
          contextMessages.push({
            type: "text",
            text: `Asset URL: ${url}`,
            isHidden: true,
          });
          contextMessages.push({
            type: "image",
            image: resizedImageUrl(url),
            isValidChatAsset,
          });
        }
      });

      inlineAssets.forEach(({ url, isValidChatAsset, isVideo }) => {
        if (isVideo) {
          contextMessages.push({
            type: "text",
            text: `[Attached Video File] The user has attached a video file. Video URL: ${url}`,
            isHidden: true,
          });
          contextMessages.push({
            type: "video",
            video: url,
          });
        } else {
          contextMessages.push({
            type: "image",
            image: resizedImageUrl(url),
            isHidden: true,
            isValidChatAsset,
          });
        }
      });

      const numProductsAttached = segments.filter((segment) => {
        return (
          segment.type === "chat-token" && segment.token.type === "product"
        );
      }).length;

      const selectedElementToken: string | null = (() => {
        if (!defaultDesignation) {
          return null;
        }
        const token = chatTokenSchema.safeParse({
          type: "selected-element",
          data: {
            // NOTE (Max, 2026-01-14): We're not sending the innerHTML to reduce payload size (as
            // TRPC subscriptions use GET requests, and the innerHTML can cause URL length limits).
            // The message will contain the data-rid (stableElementId) which can be used by the agent
            // to find the element in the file using the readTool.
            elementType: defaultDesignation.element.type,
            stableElementId: defaultDesignation.element.stableElementId,
          },
        });
        if (!token.success) {
          return null;
        }
        return serializeChatToken(token.data);
      })();

      return {
        message: {
          id: v4(),
          content: [
            ...contextMessages,
            {
              type: "text",
              text: selectedElementToken
                ? `${selectedElementToken} ${currentPrompt}`
                : currentPrompt,
              isHidden,
              fileId: pageId ?? undefined,
            },
          ],
          createdAt: new Date(),
          feedback: "none",
          role: "user" as const,
          chatId,
          chatTurnId,
        },
        numInlineAssetsAttached: inlineAssets.length,
        numExternalAssetsAttached: externalAssets.length,
        numProductsAttached,
        numSelectedElementsAttached: 0,
      };
    },
    [pageId],
  );

  const sendChatRequest = useCallback(
    async ({
      prompt,
      isHidden,
      defaultDesignation,
      filesToUpload,
    }: {
      prompt: string;
      isHidden: boolean;
      defaultDesignation?: SelectedElementDesignationElementTransformed;
      filesToUpload?: ChatFileAttachment[] | null;
    }) => {
      if (isChatLoading || !projectId || !user?.id || !prompt) {
        return;
      }

      const chatIdToUse = chatId && chatId.length > 0 ? chatId : v4();

      // NOTE (Gabe 2026-01-29): cancellation of split chat run is handled
      // directly via handleStop & cancelChatTurn by consumer.
      setStreamingState(chatIdToUse, {
        isStreaming: true,
      });

      // NOTE (Max, 2025-12-05): Create the chat BEFORE creating the chat turn
      // to avoid foreign key constraint violations. The ChatTurn has a foreign key
      // to the Chat table, so the Chat must exist first.
      if (!chatId) {
        try {
          const newChat = await createChat({
            projectId,
            id: chatIdToUse,
          });

          if (pageId) {
            updateChatPageId({
              chatId: newChat.id,
              pageId,
            });
          }

          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("chatId", newChat.id);
            return newParams;
          });

          setIsChatSidebarOpen(false);
        } catch {
          updateStreamingStatus(chatIdToUse, false);
          return;
        }
      } else {
        if (pageId) {
          updateChatPageId({
            chatId,
            pageId,
          });
        }
      }

      const { chatTurnId } = await addChatTurn({
        chatId: chatIdToUse,
      });

      const {
        message: newUserMessage,
        numInlineAssetsAttached,
        numExternalAssetsAttached,
        numProductsAttached,
        numSelectedElementsAttached,
      } = await createNewUserMessage({
        currentPrompt: prompt,
        isHidden,
        chatId: chatIdToUse,
        projectId,
        defaultDesignation,
        filesToUpload,
        chatTurnId,
      });
      logEvent("chat.send", {
        origin: "chat",
        userPrompt: prompt,
        numInlineAssetsAttached,
        numExternalAssetsAttached,
        numProductsAttached,
        numSelectedElementsAttached,
      });

      if (newUserMessage) {
        addOrUpdateMessageForChat(
          chatIdToUse,
          newUserMessage.id,
          newUserMessage.content,
          newUserMessage.role,
          newUserMessage.chatTurnId,
        );
      }

      void workspaceUtils.getChatMessagesUsage.invalidate();

      try {
        if (
          chatIdToUse &&
          (!chat?.title || chat.title === "Untitled") &&
          projectId
        ) {
          generateTitle({
            id: chatIdToUse,
            initialPrompt: prompt,
          });
        }

        // Force scroll to bottom to show the new message after it's added
        if (forceScrollToBottomRef.current) {
          forceScrollToBottomRef.current();
        }

        const contextObject = {
          openPageId: pageId,
        };

        if (isAgentBillingEnabled && workspaceId) {
          await check({
            workspaceId,
            featureType: "credits",
          });
          await track({
            workspaceId,
            featureType: "credits",
          });
        }

        // NOTE (Gabe, 2026-01-27): Split streaming flow - initialize first, then subscribe
        await initializeChatTurn({
          chatTurnId,
          newUserMessage,
          contextObject,
          useOnlinePageGeneration: true,
          useOpusModel: isFeatureEnabled("replo-agent-opus"),
        });

        splitStreamStartedRef.current = chatTurnId;
        setSplitStreamParams({
          chatTurnId,
          chatId: chatIdToUse,
        });
      } catch (error) {
        if (error instanceof TRPCClientError) {
          // eslint-disable-next-line replo/no-as-cast -- (Noah, 2026-07-20): scoped replacement for an existing broad lint exception.
          const errorData = error.data as {
            userFacingDetails?: { key?: string };
          };
          const key = errorData?.userFacingDetails?.key;
          if (key?.startsWith("agentBilling.entitlements.insufficient")) {
            void workspaceUtils.getChatMessagesUsage.invalidate();
          }
        }
        updateStreamingStatus(chatIdToUse, false);
      }
    },
    [
      createChat,
      isChatLoading,
      projectId,
      user?.id,
      createNewUserMessage,
      logEvent,
      updateStreamingStatus,
      chatId,
      pageId,
      updateChatPageId,
      setSearchParams,
      chat?.title,
      generateTitle,
      setIsChatSidebarOpen,
      addOrUpdateMessageForChat,
      addChatTurn,
      setStreamingState,
      initializeChatTurn,
      isAgentBillingEnabled,
      workspaceId,
      check,
      track,
    ],
  );

  return {
    sendChatRequest,
    isGeneratingTitle,
    chatId,
    files,
    setFiles,
    chat,
    chatError,
    forceScrollToBottomRef,
  };
};
